import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  forwardRef,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  AbstractControl,
} from "@angular/forms";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { Subject } from "rxjs/Subject";
import { Subscription } from "rxjs/Subscription";
import { from } from "rxjs/observable/from";
import { of } from "rxjs/observable/of";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
} from "rxjs/operators";

type SearchFnResult =
  | Observable<string[]>
  | Promise<string[]>
  | string[]
  | string
  | null
  | undefined;

type SearchFn = (term: string) => SearchFnResult;

@Component({
    selector: "app-search-typeahead",
    templateUrl: "./search-typeahead.component.html",
    styleUrls: ["./search-typeahead.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchTypeaheadComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SearchTypeaheadComponent),
            multi: true,
        },
    ],
})
export class SearchTypeaheadComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @Input() placeholder = "";
  @Input() minTermLength = 1;
  @Input() loadingText = "Loading...";
  @Input() noResultsText = "No results found";
  @Input() openOnFocus = true;
  @Input() clearable = true;
  @Input() allowCustomValue = true;
  @Input() name = "";
  @Input() appendTo?: string;
  @Input() required = false;
  @Input() searchFn: SearchFn | null = null;

  @Output() valueSelected = new EventEmitter<string>();
  @Output() valueCleared = new EventEmitter<void>();

  readonly typeahead$ = new Subject<string>();
  readonly items$ = new BehaviorSubject<string[]>([]);

  items: string[] = [];
  value: string | null = null;
  loading = false;
  disabled = false;

  private readonly subscription = new Subscription();
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscription.add(
      this.typeahead$
        .pipe(
          debounceTime(200),
          distinctUntilChanged(),
          switchMap((term) => this.fetchItems(term || ""))
        )
        .subscribe((results) => {
          this.items = results;
          this.items$.next(results);
          this.ensureValueVisible();
          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.typeahead$.complete();
    this.items$.complete();
  }

  handleFocus(): void {
    const currentValue = (this.value || "").trim();
    if (this.openOnFocus && currentValue.length >= this.minTermLength) {
      this.typeahead$.next(currentValue);
    }
    this.onTouched();
  }

  handleBlur(): void {
    this.onTouched();
  }

  handleChange(nextValue: string | null): void {
    const normalized = typeof nextValue === "string" ? nextValue : null;
    this.value = normalized;
    this.ensureValueVisible();
    this.onChange(normalized);
    this.valueSelected.emit(normalized || "");
    this.onValidatorChange();
  }

  handleAddTag(term: string): string {
    const trimmed = term ? term.trim() : "";
    if (!trimmed) {
      return "";
    }
    if (!this.items.includes(trimmed)) {
      this.items = [trimmed, ...this.items];
      this.items$.next(this.items);
    }
    return trimmed;
  }

  handleClear(): void {
    this.value = null;
    this.onChange(null);
    this.valueCleared.emit();
    this.cdr.markForCheck();
    this.onValidatorChange();
  }

  writeValue(value: string | null): void {
    this.value = typeof value === "string" ? value : null;
    this.ensureValueVisible();
    this.cdr.markForCheck();
    this.onValidatorChange();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) {
      return null;
    }
    return this.value ? null : { required: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private fetchItems(term: string): Observable<string[]> {
    const trimmed = term.trim();
    if (!this.searchFn || (trimmed.length < this.minTermLength && trimmed.length !== 0)) {
      if (trimmed.length === 0) {
        return of([]);
      }
      return of([]);
    }

    const result = this.searchFn(trimmed);
    this.loading = true;
    this.cdr.markForCheck();

    return this.coerceToObservable(result).pipe(
      map((items) => this.normalizeItems(items)),
      catchError(() => of([])),
      finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      })
    );
  }

  private coerceToObservable(result: SearchFnResult): Observable<string[]> {
    if (result && typeof (result as any).subscribe === "function") {
      return result as Observable<string[]>;
    }
    if (result instanceof Promise) {
      return from(result as Promise<string[]>);
    }
    if (Array.isArray(result)) {
      return of(result);
    }
    if (typeof result === "string") {
      return of([result]);
    }
    return of([]);
  }

  private normalizeItems(items: string[] | any): string[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.filter((item) => typeof item === "string");
  }

  private ensureValueVisible(): void {
    if (!this.value) {
      return;
    }
    if (!this.items.includes(this.value)) {
      this.items = [this.value, ...this.items];
      this.items$.next(this.items);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/search-typeahead/search-typeahead.component.ts